import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, ArrowRight, BookOpen, CircleCheck, Lightbulb, Loader2, TriangleAlert, X } from 'lucide-react';
import { fetchRoadCodeLesson, fetchRoadCodeLessons, markRoadCodeLessonRead, ROAD_CODE_KEY } from '../../api/roadCode';
import { Badge, Button, EmptyState, ProgressBar, cx } from '../../components/charte';
import type { LessonBlock, LessonCatalog } from '../../lib/roadCode';
import { FullScreen } from './SeriesPlayer';
import { RoadCodeMediaView } from './RoadCodeMediaView';
import { SourcesLine } from './SourcesLine';
import { ThemeIcon } from './ThemeSheet';

const LESSONS_KEY = [...ROAD_CODE_KEY, 'lessons'] as const;

/**
 * Fiches de cours rangées par thème, en cartes à faire défiler (une rangée par thème) avec leur
 * avancement (« 2/5 »). Sans fiche publiée : un état vide, la révision par questions reste ouverte.
 */
export function LessonsList({ onBack, onOpen }: { onBack: () => void; onOpen: (lessonId: string) => void }) {
  const query = useQuery({ queryKey: LESSONS_KEY, queryFn: fetchRoadCodeLessons });
  const themes = query.data?.themes ?? [];
  return (
    <div className="grid gap-5">
      <div className="flex items-center gap-2">
        <Button variant="ghost" iconOnly icon={ArrowLeft} aria-label="Retour à l’accueil du code" onClick={onBack} className="-ml-2" />
        <h1 className="text-title-md text-heading">Fiches de cours</h1>
      </div>
      {query.isPending && (
        <p className="flex items-center gap-2 text-sm text-secondary">
          <Loader2 size={16} className="animate-spin" aria-hidden /> Chargement des fiches…
        </p>
      )}
      {query.isError && <p className="text-sm text-danger-dark">Fiches indisponibles pour le moment. Réessayez plus tard.</p>}
      {query.isSuccess && !themes.length && (
        <EmptyState
          icon={BookOpen}
          title="Les fiches de cours arrivent bientôt"
          description="En attendant, entraînez-vous avec les séries de questions : chaque correction explique la règle."
        />
      )}
      {themes.map((theme) => (
        <section key={theme.theme} aria-labelledby={`fiches-${theme.theme}`}>
          <div className="mb-3 flex items-center gap-3">
            <ThemeIcon theme={theme.theme} size="sm" />
            <h2 id={`fiches-${theme.theme}`} className="text-title-sm text-heading">
              {theme.label}
            </h2>
          </div>
          <ul className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 lg:grid-cols-3">
            {theme.lessons.map((lesson) => {
              const done = lesson.read >= lesson.sections;
              return (
                <li key={lesson.id} className="w-[72%] shrink-0 snap-start sm:w-auto">
                  <button
                    type="button"
                    onClick={() => onOpen(lesson.id)}
                    className="flex h-full min-h-[150px] w-full flex-col rounded-card border border-line bg-white p-4 text-left transition-all hover:border-primary-200 hover:shadow-navbar focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
                  >
                    <Badge tone={done ? 'ok' : 'mute'} className="self-start">
                      {done ? 'Lue' : `${lesson.sections} partie${lesson.sections > 1 ? 's' : ''}`}
                    </Badge>
                    <span className="mt-2.5 flex-1 text-[16px] font-semibold leading-snug text-heading">{lesson.title}</span>
                    <span className="mt-3 flex items-center gap-2.5">
                      <span className="flex-1">
                        <ProgressBar
                          value={(lesson.read / lesson.sections) * 100}
                          tone={done ? 'success' : 'primary'}
                          label={`${lesson.read} parties lues sur ${lesson.sections}`}
                        />
                      </span>
                      <span className="text-[12.5px] tabular-nums text-secondary">
                        {lesson.read}/{lesson.sections}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
}

function Block({ block }: { block: LessonBlock }) {
  if (block.type === 'text') return <p className="text-[15.5px] leading-relaxed text-body">{block.text}</p>;
  if (block.type === 'list')
    return (
      <ul className="grid gap-2">
        {block.items.map((item) => (
          <li key={item} className="flex gap-2.5 text-[15.5px] leading-relaxed text-body">
            <span className="mt-[9px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
            {item}
          </li>
        ))}
      </ul>
    );
  if (block.type === 'media') return <RoadCodeMediaView media={block} className="rounded-row" />;
  const tip = block.type === 'tip';
  const Icon = tip ? Lightbulb : TriangleAlert;
  return (
    <div className={cx('flex gap-2.5 rounded-row px-4 py-3 text-[14.5px] leading-relaxed', tip ? 'bg-surface-indigo text-primary-900' : 'bg-warn-soft text-warn-fg')}>
      <Icon size={18} className={cx('mt-0.5 shrink-0', tip ? 'text-primary' : 'text-warn-line')} aria-hidden />
      <p>
        <span className="font-semibold">{tip ? 'Bon à savoir : ' : 'Attention : '}</span>
        {block.text}
      </p>
    </div>
  );
}

/**
 * Lecture d'une fiche, une partie par écran (à la façon d'une histoire) : avancement en haut,
 * « Précédent » et « Suivant » sous le pouce. Chaque partie atteinte est enregistrée (la progression
 * ne recule jamais) ; la dernière propose la fiche suivante du thème.
 */
export function LessonReader({ lessonId, onClose, onOpen }: { lessonId: string; onClose: () => void; onOpen: (lessonId: string) => void }) {
  const client = useQueryClient();
  const query = useQuery({ queryKey: [...LESSONS_KEY, lessonId], queryFn: () => fetchRoadCodeLesson(lessonId) });
  const lesson = query.data;
  const [index, setIndex] = useState(0);
  const started = useRef(false);
  // Reprise à la première partie non lue (ou au début si tout est lu).
  useEffect(() => {
    if (!lesson || started.current) return;
    started.current = true;
    setIndex(lesson.read > 0 && lesson.read < lesson.sections.length ? lesson.read : 0);
  }, [lesson]);
  const read = useMutation({
    mutationFn: (sections: number) => markRoadCodeLessonRead(lessonId, sections),
    onSuccess: (data) => {
      client.setQueryData<LessonCatalog>(LESSONS_KEY, (catalog) =>
        catalog
          ? { themes: catalog.themes.map((t) => ({ ...t, lessons: t.lessons.map((l) => (l.id === data.id ? { ...l, read: data.read } : l)) })) }
          : catalog,
      );
    },
  });
  // Partie affichée : comptée comme lue (envoyée une fois par partie).
  const sent = useRef(0);
  useEffect(() => {
    if (!lesson) return;
    const reached = index + 1;
    if (reached > Math.max(sent.current, lesson.read)) {
      sent.current = reached;
      read.mutate(reached);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesson, index]);
  const main = useRef<HTMLElement>(null);
  useEffect(() => {
    main.current?.scrollTo({ top: 0 });
  }, [index]);

  const total = lesson?.sections.length ?? 1;
  const section = lesson?.sections[index];
  const last = index === total - 1;
  return (
    <FullScreen label={lesson ? `Fiche : ${lesson.title}` : 'Fiche de cours'} onEscape={onClose}>
      <header className="shrink-0 border-b border-line bg-white safe-top">
        <div className="mx-auto flex h-14 max-w-2xl items-center gap-3 px-3">
          <Button variant="ghost" iconOnly icon={X} aria-label="Fermer la fiche" onClick={onClose} />
          <div className="min-w-0 flex-1 text-center">
            <p className="truncate text-[12px] font-medium text-secondary">{lesson?.label ?? 'Fiche de cours'}</p>
            <h1 className="truncate text-[15px] font-semibold text-heading">{lesson?.title ?? '…'}</h1>
          </div>
          <span className="w-10 text-center text-[12.5px] tabular-nums text-secondary">
            {index + 1}/{total}
          </span>
        </div>
        {/* Une barre par partie, à la façon des histoires. */}
        <div className="mx-auto flex max-w-2xl gap-1 px-4 pb-2" aria-hidden>
          {Array.from({ length: total }, (_, i) => (
            <span key={i} className={cx('h-1 flex-1 rounded-pill', i <= index ? 'bg-primary' : 'bg-surface-2')} />
          ))}
        </div>
      </header>
      <main ref={main} className="min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto grid max-w-2xl gap-4 px-4 py-5">
          {query.isPending && (
            <p className="flex items-center gap-2 text-sm text-secondary">
              <Loader2 size={16} className="animate-spin" aria-hidden /> Chargement…
            </p>
          )}
          {query.isError && <p className="text-sm text-danger-dark">Fiche indisponible pour le moment.</p>}
          {section && (
            <>
              <h2 className="text-title-md text-heading">{section.title}</h2>
              {section.blocks.map((block, i) => (
                <Block key={i} block={block} />
              ))}
            </>
          )}
          {lesson && last && (
            <>
              <div className="mt-2 flex items-center gap-2 rounded-row bg-success-light px-4 py-3 text-[14px] font-medium text-success-dark">
                <CircleCheck size={18} aria-hidden /> Fiche terminée
              </div>
              <SourcesLine sources={lesson.sources} />
            </>
          )}
        </div>
      </main>
      {lesson && (
        <footer className="shrink-0 border-t border-line bg-white pb-[max(12px,env(safe-area-inset-bottom))]">
          <div className="mx-auto flex max-w-2xl gap-2 px-4 pt-3">
            <Button icon={ArrowLeft} className="h-12" disabled={index === 0} onClick={() => setIndex((i) => Math.max(0, i - 1))}>
              Précédent
            </Button>
            {!last && (
              <Button variant="primary" iconEnd={ArrowRight} className="h-12 flex-1" onClick={() => setIndex((i) => Math.min(total - 1, i + 1))}>
                Suivant
              </Button>
            )}
            {last && lesson.next && (
              <Button variant="primary" iconEnd={ArrowRight} className="h-12 flex-1" onClick={() => onOpen(lesson.next!.id)}>
                Fiche suivante
              </Button>
            )}
            {last && !lesson.next && (
              <Button variant="primary" className="h-12 flex-1" onClick={onClose}>
                Terminer
              </Button>
            )}
          </div>
        </footer>
      )}
    </FullScreen>
  );
}
